import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { invoicesApi } from "@web-src/features/api/createApi";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import {
  CreateInvoicePayload,
  UpdateInvoicePayload,
  InvoiceType,
  InvoiceModel,
  InventoryItem,
  InvoiceLineItem,
  InventoryItemCategory,
} from "@jugl-web/rest-api";
import {
  useBlockNavigation,
  useToast,
  useTranslations,
  dateToISOWithoutTimezone,
} from "@jugl-web/utils";
import { v4 as uuidv4 } from "uuid";
import { useParams } from "react-router-dom";
import { mapInvoiceTypeToPrefix } from "@jugl-web/rest-api/invoices/dictionary";
import { useInvoiceTypeToLabelDictionary } from "@jugl-web/rest-api/invoices/hooks/useInvoiceTypeToLabelDictionary";
import addDays from "date-fns/addDays";
import startOfDay from "date-fns/startOfDay";
import { useTaxFields } from "../../InvoicesSettingsPage/hooks/useTaxFields";
import { useInvoicesConfigList } from "../../InvoicesSettingsPage/pages/InvoicesBusinessInformationPage/hooks/useInvoicesConfigList";
import {
  InvoiceItemFormFields,
  InvoicesItemFormPageContext,
} from "./InvoicesItemFormPageContext";
import { getCurrencySymbol } from "../components/InvoiceForm/components/CurrencyField/utils";
import { InvoicesItemDiscardAlert } from "../components/InvoicesItemDiscardAlert";

type InvoicesItemFormPageProviderProps = {
  id?: string;
  children: React.ReactNode;
};

const getEmptyItem = (): InvoiceLineItem => ({
  inventory_id: null,
  name: "",
  desc: "",
  qty: 1,
  price: 0,
  category: InventoryItemCategory.service,
  currency: null,
  total: 0,
  unit: null,
  tax: [],
  discount: null,
  price_type: null,
});

export const InvoicesItemFormPageProvider = ({
  children,
  id,
}: InvoicesItemFormPageProviderProps) => {
  const confirmNavigation = useRef<(() => void) | null>(null);
  const skipNavigationAlert = useRef(false);
  const [isDiscardChangesDialogOpen, setIsDiscardChangesDialogOpen] =
    useState(false);

  useBlockNavigation(
    setIsDiscardChangesDialogOpen,
    confirmNavigation,
    skipNavigationAlert
  );

  const { type: invoiceType } = useParams<{ type: InvoiceType }>();

  const { t } = useTranslations();
  const { toast } = useToast({ variant: "web" });
  const dict = useInvoiceTypeToLabelDictionary();

  const [invoiceLevelDiscount, setInvoiceLevelDiscount] = useState<{
    type: string;
    amount: number | null;
  }>({
    type: "percentage",
    amount: null,
  });

  const {
    taxFields,
    selectedTaxes,
    handleAddTaxField: onAddTaxField,
    handleEditTaxField: onEditTaxField,
    handleRemoveTaxField: onRemoveTaxField,
    handleSetDefaultTaxField: onSetDefaultTaxField,
    handleSetAsSelected: onSetAsSelected,
    handleRemoveFromSelected: onRemoveFromSelected,
  } = useTaxFields();

  const [customErrors, setCustomErrors] = useState<{
    items?: string;
    quantity?: {
      [itemId: string]: string;
    };
  }>({});
  const { configId } = useInvoicesConfigList();
  const { entityId, entity } = useEntitySelectedProvider();

  const { data: globalSettings } = invoicesApi.useGetConfigQuery({
    entityId,
    configId,
  });

  const { data: invoiceData } = invoicesApi.useGetInvoiceQuery({
    entityId,
    invoiceId: id ?? "",
  });

  const [createInvoice, { isLoading: isCreatingInvoice }] =
    invoicesApi.useCreateInvoiceMutation();
  const [updateInvoice, { isLoading: isUpdatingInvoice }] =
    invoicesApi.useUpdateInvoiceMutation();

  const {
    register,
    formState: { errors },
    watch,
    setValue,
    handleSubmit,
    control,
  } = useForm<InvoiceItemFormFields>({
    mode: "onChange",
    defaultValues: {
      payment_type: "cash",
      currency: entity.currency,
      date: undefined,
      customer_id: null,
      status: "issued",
      due_on: undefined,
      billFrom: "",
      billTo: "",
      notes: "",
    },
  });

  const itemsField = useFieldArray({
    control,
    name: "items",
  });

  const formValues = watch();

  const setFieldValue = useCallback(
    (field: keyof InvoiceItemFormFields, value: string | null) => {
      setValue(field, value ?? "");
      skipNavigationAlert.current = false;
    },
    [setValue]
  );

  const resetBillFromField = useCallback(() => {
    const settings = globalSettings?.data;

    if (!settings) return;

    const { header } = settings;

    if (header) {
      setValue(
        "billFrom",
        [
          header.company_name ?? "",
          header.street ?? "",
          `${header.city ? `${header.city}, ` : ""}${header.state ?? ""} ${
            header.zip_code ?? ""
          } `,
          header.country ?? "",
          Object.entries(header.custom_field ?? {})
            .map(([, value]) => `${value}`)
            .join("\n"),
        ].join("\n") || ""
      );
    }
  }, [globalSettings, setValue]);

  const populateTaxFields = useCallback(() => {
    if (globalSettings?.data.tax) {
      globalSettings.data.tax.forEach((tax) => {
        if (tax.type === "item") {
          onAddTaxField("item", {
            id: uuidv4(),
            name: tax.name,
            percentage: tax.percentage,
            default: tax.default ?? false,
            selected: id ? false : tax.default,
          });
        }

        if (tax.type === "total") {
          onAddTaxField("invoice", {
            id: uuidv4(),
            name: tax.name,
            percentage: tax.percentage,
            default: tax.default ?? false,
            selected: id ? false : tax.default,
          });
        }
      });
    }
  }, [globalSettings?.data?.tax, id, onAddTaxField]);

  const initFieldsInCreateMode = useCallback(() => {
    resetBillFromField();
    setValue("date", dateToISOWithoutTimezone(startOfDay(new Date())));
    setValue(
      "due_on",
      dateToISOWithoutTimezone(
        addDays(startOfDay(new Date()), globalSettings?.data?.due_in || 7)
      )
    );
    setValue("notes", globalSettings?.data?.footer?.notes ?? "");
  }, [globalSettings, resetBillFromField, setValue]);

  const isInitialized = useRef(false);

  const initFieldsInEditMode = useCallback(() => {
    if (!id || !invoiceData || isInitialized.current) return;

    if (invoiceData.payment_type) {
      setValue("payment_type", invoiceData.payment_type);
    }

    if (invoiceData.currency) {
      setValue("currency", invoiceData.currency?.toUpperCase());
    }

    if (invoiceData.date) {
      setValue("date", invoiceData.date);
    }

    if (invoiceData.due_on) {
      setValue("due_on", invoiceData.due_on);
    }

    setValue("status", invoiceData.status || "issued");
    if (invoiceData.bill_from) {
      setValue(
        "billFrom",
        Object.values(invoiceData.bill_from).join("\n") || ""
      );
    }
    if (invoiceData.bill_to) {
      setValue("billTo", Object.values(invoiceData.bill_to).join("\n") || "");
    }

    const discount = invoiceData.discount?.[0];
    if (discount) {
      setInvoiceLevelDiscount({
        type: discount.name,
        amount:
          discount.name === "percentage" ? discount.percentage : discount.value,
      });
    }

    setValue("customer_id", invoiceData.customer_id || "");
    setValue("notes", invoiceData.notes || "");
    setValue("items", invoiceData.items || []);

    const invoiceTaxes = invoiceData.tax?.filter(Boolean);
    invoiceTaxes?.forEach((invoiceTax) => {
      onAddTaxField("invoice", {
        id: uuidv4(),
        name: invoiceTax.name,
        percentage: invoiceTax.percentage,
        default: false,
        selected: true,
      });
    });
  }, [id, invoiceData, onAddTaxField, setValue]);

  useEffect(() => {
    if (isInitialized.current) return;
    if (id && invoiceData) {
      populateTaxFields();
      initFieldsInEditMode();
      isInitialized.current = true;
    } else if (!id) {
      populateTaxFields();
      initFieldsInCreateMode();
      isInitialized.current = true;
    }
  }, [
    globalSettings?.data,
    id,
    initFieldsInCreateMode,
    initFieldsInEditMode,
    invoiceData,
    populateTaxFields,
    setValue,
  ]);

  const formValuesToRequestPayload = useCallback(():
    | CreateInvoicePayload
    | UpdateInvoicePayload => {
    const payload: CreateInvoicePayload | UpdateInvoicePayload = {
      customer_id: formValues.customer_id ?? "",
      bill_to: formValues.billTo.split("\n").reduce((acc, line, index) => {
        acc[`line${index + 1}`] = line;
        return acc;
      }, {} as Record<string, string>),
      payment_type: formValues.payment_type,
      config_id: configId || null,
      type: invoiceType ?? invoiceData?.type ?? "invoice",
      status: formValues.status,
      currency: formValues.currency.toLowerCase(),
      date: formValues.date,
      due_on: formValues.due_on,
      bill_from: formValues.billFrom.split("\n").reduce((acc, line, index) => {
        acc[`line${index + 1}`] = line;
        return acc;
      }, {} as Record<string, string>),
      items:
        formValues.items
          ?.filter((item) => !!item.name.trim())
          ?.map((item) => {
            const result = {
              ...item,
              total: item.price * item.qty,
              currency: formValues.currency.toLowerCase(),
            };
            if (!item.unit) {
              item.unit = "qty";
            }
            item.discount = {
              name: "discount",
              percentage: 0,
            };
            if ("id" in result) {
              delete result.id;
            }
            return result;
          }) || [],
      tax: selectedTaxes.invoice.reduce((acc, taxId) => {
        const tax = taxFields.invoice.find((taxField) => taxField.id === taxId);

        if (!tax) return acc;

        return [
          ...acc,
          {
            name: tax?.name ?? "",
            percentage: tax?.percentage ?? 0,
            type: "total",
          },
        ];
      }, [] as { name: string; percentage: number; type: string }[]),
      discount: invoiceLevelDiscount.amount
        ? [
            {
              name: invoiceLevelDiscount.type,
              [invoiceLevelDiscount.type]: invoiceLevelDiscount.amount ?? 0,
            },
          ]
        : [],
      notes: formValues.notes,
      prefix: `${mapInvoiceTypeToPrefix(
        invoiceType ?? invoiceData?.type ?? "invoice",
        new Date(formValues.date ?? new Date()).getFullYear()
      )}`,
    };
    return payload;
  }, [
    configId,
    formValues,
    invoiceData?.type,
    invoiceLevelDiscount,
    invoiceType,
    selectedTaxes.invoice,
    taxFields.invoice,
  ]);

  const submitForm = useCallback(
    async () =>
      new Promise<InvoiceModel | null>((resolve) => {
        setCustomErrors({});
        handleSubmit(async () => {
          if (
            itemsField.fields.filter((item) => !!item.name.trim()).length === 0
          ) {
            setCustomErrors({
              items: t({
                id: "invoices.item.items-required",
                defaultMessage: "Items are required",
              }),
            });
            resolve(null);
            return;
          }

          const itemsWithZeroQuantity = itemsField.fields.filter(
            (item) => item.qty === 0
          );
          if (itemsWithZeroQuantity.length > 0) {
            const quantityErrors = itemsWithZeroQuantity.reduce((acc, item) => {
              acc[item.id] = t({
                id: "invoices.item.quantity-required",
                defaultMessage: "All items must have quantity greater than 0",
              });
              return acc;
            }, {} as { [key: string]: string });

            setCustomErrors({
              quantity: quantityErrors,
            });
            resolve(null);
            return;
          }
          const formData = formValuesToRequestPayload();
          if (id) {
            const res = await updateInvoice({
              entityId,
              invoiceId: id,
              payload: formData,
            });

            if (!("error" in res)) {
              skipNavigationAlert.current = true;
              toast(
                t(
                  {
                    id: "invoices.single.update-success",
                    defaultMessage: "{itemType} updated successfully",
                  },
                  {
                    itemType: dict[invoiceData?.type ?? "invoice"],
                  }
                ),
                {
                  variant: "success",
                }
              );
              resolve(res.data);
              return;
            }
            resolve(null);
            return;
          }
          const res = await createInvoice({
            entityId,
            payload: {
              ...formData,
              type: invoiceType ?? "invoice",
            },
          });

          if (!("error" in res)) {
            skipNavigationAlert.current = true;
            toast(
              t(
                {
                  id: "invoices.single.create-success",
                  defaultMessage: "{itemType} created successfully",
                },
                {
                  itemType: dict[invoiceType ?? "invoice"],
                }
              ),
              { variant: "success" }
            );
            resolve(res.data);
            return;
          }
          resolve(null);
        })();
      }),
    [
      handleSubmit,
      itemsField.fields,
      formValuesToRequestPayload,
      id,
      createInvoice,
      entityId,
      invoiceType,
      t,
      updateInvoice,
      toast,
      dict,
      invoiceData?.type,
    ]
  );

  const currencySymbol = getCurrencySymbol(formValues.currency);

  const invoiceItemData = useMemo((): Partial<InvoiceModel> => {
    const invoiceItem =
      // TODO: fix later
      formValuesToRequestPayload() as unknown as Partial<InvoiceModel>;

    if (invoiceData) {
      invoiceItem.no = invoiceData.no;
      invoiceItem.id = invoiceData.id;
      invoiceItem.short_url = invoiceData.short_url || "";
    }
    invoiceItem.type = invoiceData?.type ? invoiceData.type : invoiceType;
    invoiceItem.total = itemsField.fields.reduce(
      (acc, item) => acc + item.price * item.qty,
      0
    );
    let grandTotal =
      invoiceItem.total -
      (invoiceItem.discount?.reduce(
        (acc, curr) =>
          acc + curr.percentage
            ? (invoiceItem.total || 0) * (curr.percentage / 100)
            : curr.value || 0,
        0
      ) || 0);
    if (invoiceItem.tax) {
      grandTotal += invoiceItem.tax.reduce(
        (acc, curr) => acc + (grandTotal || 0) * (curr.percentage / 100),
        0
      );
    }
    invoiceItem.grand_total = grandTotal;
    return invoiceItem;
  }, [formValuesToRequestPayload, invoiceData, invoiceType, itemsField.fields]);

  const addInventoryItem = useCallback(
    (item: InventoryItem, qty: number, idx?: number) => {
      const fromInventory: InvoiceLineItem = {
        inventory_id: item.id,
        name: item.name.trim(),
        desc: "",
        qty: qty || 1,
        price: parseFloat(item.price) || 0,
        category: item.category as InventoryItemCategory,
        currency: null,
        total: 0,
        unit: item.unit,
        tax: [],
        discount: null,
        price_type: null,
      };
      if (idx !== undefined) {
        const currentField = itemsField.fields[idx];
        if (currentField) {
          itemsField.update(idx, {
            ...currentField,
            ...fromInventory,
          });
        }
        return;
      }
      itemsField.insert(itemsField.fields.length - 1, fromInventory);
    },
    [itemsField]
  );
  useEffect(() => {
    if (itemsField.fields.length === 0) {
      itemsField.append(getEmptyItem());
      return;
    }
    const lastField = itemsField.fields[itemsField.fields.length - 1];
    if (lastField.name) {
      itemsField.append(getEmptyItem());
      return;
    }
    const beforeLastField = itemsField.fields[itemsField.fields.length - 2];
    if (
      beforeLastField &&
      !beforeLastField?.name &&
      lastField &&
      !lastField?.name
    ) {
      itemsField.remove(itemsField.fields.length - 1);
    }
  }, [itemsField]);

  const value = useMemo(
    () => ({
      formValues,
      setFieldValue,
      register,
      errors,
      watch,
      submitForm,
      globalSettings: globalSettings?.data,
      currencySymbol: currencySymbol ?? "",
      resetBillFromField,
      invoiceLevelDiscount,
      setInvoiceLevelDiscount,
      taxFields,
      selectedTaxes,
      onAddTaxField,
      onEditTaxField,
      onRemoveTaxField,
      onSetDefaultTaxField,
      onSetAsSelected,
      onRemoveFromSelected,
      id,
      savedStatus: invoiceData?.status,
      type: invoiceType ?? invoiceData?.type ?? "invoice",
      invoiceItemData,
      customErrors,
      setCustomErrors,
      skipNavigationAlert,
      isSubmitting: isCreatingInvoice || isUpdatingInvoice,
      itemsField,
      addInventoryItem,
    }),
    [
      submitForm,
      formValues,
      setFieldValue,
      register,
      errors,
      watch,
      globalSettings?.data,
      currencySymbol,
      resetBillFromField,
      invoiceLevelDiscount,
      taxFields,
      selectedTaxes,
      onAddTaxField,
      onEditTaxField,
      onRemoveTaxField,
      onSetDefaultTaxField,
      onSetAsSelected,
      onRemoveFromSelected,
      id,
      invoiceData?.status,
      invoiceData?.type,
      invoiceType,
      invoiceItemData,
      customErrors,
      isCreatingInvoice,
      isUpdatingInvoice,
      itemsField,
      addInventoryItem,
    ]
  );
  return (
    <InvoicesItemFormPageContext.Provider value={value}>
      {children}
      <InvoicesItemDiscardAlert
        isOpen={isDiscardChangesDialogOpen}
        onCancel={() => {
          confirmNavigation.current = null;
          setIsDiscardChangesDialogOpen(false);
        }}
        onConfirm={() => {
          setIsDiscardChangesDialogOpen(false);
          confirmNavigation.current?.();
        }}
        onRequestClose={() => {
          setIsDiscardChangesDialogOpen(false);
        }}
      />
    </InvoicesItemFormPageContext.Provider>
  );
};
